/**
 * Hero Video — lazy, motion-aware background video for .c-hero-home
 *
 * The markup ships with `preload="none"` and `<source data-src>` (no real `src`),
 * so the browser never downloads the video up front — the poster is all that
 * paints on first load, keeping the file out of the LCP/critical path.
 *
 * When the hero scrolls into view we pick the sources matching the current
 * viewport (data-media, mirroring the CSS 1024px breakpoint; mobile falls back to
 * the desktop sources), swap `data-src` → `src`, load, and play. Format choice
 * (WebM vs MP4) is left to the browser via each source's `type`.
 *
 * Playback is gated on prefers-reduced-motion: reduced-motion visitors keep the
 * static poster. We also pause when the hero leaves the viewport to save CPU
 * and battery, resuming on re-entry.
 */

// Mirror the CSS: below 1024px is the "mobile" layout.
const isMobileViewport = () => !window.matchMedia('(min-width: 1024px)').matches;

function activate(video) {
  if (video.dataset.heroActivated) return;
  video.dataset.heroActivated = 'true';

  const sources = [...video.querySelectorAll('source[data-src]')];
  const mobile = sources.filter((s) => s.dataset.media === 'mobile');
  const desktop = sources.filter((s) => s.dataset.media !== 'mobile');

  // Use the mobile encode on small screens when present; otherwise the desktop
  // set. Either group may be empty, so fall back to whatever exists.
  let chosen = isMobileViewport()
    ? (mobile.length ? mobile : desktop)
    : (desktop.length ? desktop : mobile);

  sources.forEach((source) => {
    if (!chosen.includes(source)) {
      source.remove();
      return;
    }
    source.src = source.dataset.src;
    source.removeAttribute('data-src');
  });

  video.load();
}

function initHeroVideo() {
  const videos = document.querySelectorAll('[data-hero-video]');
  if (!videos.length) return;

  // Reduced-motion visitors keep the poster — never load or play the video.
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        const video = entry.target;
        if (entry.isIntersecting) {
          activate(video);
          video.play().catch(() => {
            /* Autoplay can still be refused (e.g. Low Power Mode); poster remains. */
          });
        } else if (video.dataset.heroActivated) {
          video.pause();
        }
      });
    },
    { rootMargin: '200px' }
  );

  videos.forEach((video) => observer.observe(video));
}

document.addEventListener('DOMContentLoaded', initHeroVideo);
